import {
    computed,
    DestroyRef,
    inject,
    Injectable,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    createHttpContextFromOptions,
    toLocalDate,
} from '@quipex/core/primitives';
import type { ResolvedProblemDetails } from '@quipex/core/utils';
import {
    createQuery,
    readQuery,
    qpxToast as toast,
    type QuerySettledStatus,
} from '@quipex/core/utils';
import { UsersApiService, type GetMeResponse } from '@quipex/shared/contracts';
import type { Observable } from 'rxjs';
import { EMPTY, firstValueFrom, Subject, takeUntil } from 'rxjs';
import {
    catchError,
    filter,
    map,
    pairwise,
    startWith,
    switchMap,
    take,
    tap,
} from 'rxjs/operators';
import type { AuthenticatedAccount } from '../auth.service';
import type { CurrentOrganisation, CurrentUser } from './models';

@Injectable({
    providedIn: 'root',
})
export class UserStore {
    // #region Dependency injection
    private readonly destroyRef = inject(DestroyRef);
    private readonly usersApi = inject(UsersApiService);
    // #endregion

    // #region User state
    private readonly authenticatedAccountSub = signal<string | null>(null);

    private readonly _meQuery = createQuery<
        CurrentUser,
        ResolvedProblemDetails
    >();
    public readonly meQuery = readQuery(this._meQuery);
    public readonly me = this.meQuery.data;

    public readonly me$ = toObservable(this.me);
    public readonly meStatus$ = toObservable(this.meQuery.liveStatus);
    public readonly meIsFetching$ = toObservable(this.meQuery.isFetching);
    // #endregion

    // #region Organization state
    private readonly organisationContext = signal<{
        id: string;
        name: string;
    } | null>(null);

    public readonly org = computed<CurrentOrganisation | null>(() => {
        const context = this.organisationContext();
        if (!context) {
            return null;
        }
        return {
            id: context.id,
            name: context.name,
        };
    });

    public readonly org$ = toObservable(this.org);
    // #endregion

    /** Fetch trigger Subject */
    private readonly fetchTrigger$ = new Subject<void>();
    private readonly cancelFetch$ = new Subject<void>();

    //#region Lifecycle hooks
    constructor() {
        this.setupFetching();
    }
    //#endregion

    // #region Public methods

    /** Triggers a fetch of user and organization data */
    public fetch(): void {
        this.fetchTrigger$.next();
    }

    /**
     * Ensures the cached `/me` context belongs to the current Auth0 account.
     * A changed account invalidates the cached user/org state before refetching.
     */
    public ensureFreshForAuthenticatedAccount(
        account: AuthenticatedAccount
    ): void {
        if (this.authenticatedAccountSub() !== account.sub) {
            this.authenticatedAccountSub.set(account.sub);
            this.resetContext();
        }

        if (this.meQuery.isIdle()) {
            this.fetch();
        }
    }

    /** Clears the cached user context when no authenticated Auth0 session is active. */
    public clearAuthenticatedAccount(): void {
        this.authenticatedAccountSub.set(null);
        this.resetContext();
    }

    /** Triggers a fetch and waits for outcome */
    public async fetchAsync(): Promise<QuerySettledStatus> {
        return firstValueFrom(this.fetchSettledStatusForCurrentFetch$());
    }

    /** Triggers a fetch and returns an observable that completes on success or error */
    public fetch$(): Observable<QuerySettledStatus> {
        return this.fetchSettledStatusForCurrentFetch$();
    }
    // #endregion

    // #region Private methods
    private fetchSettledStatusForCurrentFetch$() {
        return this.meIsFetching$.pipe(
            tap({ subscribe: () => this.fetch() }),
            startWith(this.meQuery.isFetching()),
            pairwise(),
            filter(([wasFetching, isFetching]) => wasFetching && !isFetching),
            map(() =>
                this.meQuery.liveStatus() === 'success' ? 'success' : 'error'
            ),
            take(1)
        );
    }

    private setupFetching(): void {
        this.fetchTrigger$
            .pipe(
                tap(() => {
                    this._meQuery.setLoading();
                }),
                switchMap(() => {
                    const context = createHttpContextFromOptions({
                        suppressAutoErrorToast: true,
                    });
                    return this.usersApi.getMe({ context }).pipe(
                        tap((response: GetMeResponse) => {
                            const me = this.transformUser(response);
                            this._meQuery.setData(me);
                            this.organisationContext.set({
                                id: response.organisationId,
                                name: response.organisationName,
                            });
                        }),
                        catchError((err: ResolvedProblemDetails) => {
                            this._meQuery.setError(err);
                            this.organisationContext.set(null);

                            if (err.status !== 404) {
                                toast.error(err.detail ?? err.title);
                            }
                            return EMPTY;
                        }),
                        takeUntil(this.cancelFetch$)
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private cancelInFlightFetch(): void {
        this.cancelFetch$.next();
    }

    private resetContext(): void {
        this.cancelInFlightFetch();
        this._meQuery.reset();
        this.organisationContext.set(null);
    }

    /** Transforms UserResponse to CurrentUser */
    private transformUser(user: GetMeResponse): CurrentUser {
        return {
            ...user,
            lastLoggedIn: user.lastLoggedIn
                ? toLocalDate(user.lastLoggedIn)
                : null,
        };
    }
    // #endregion
}
